import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(CartAddEvent.eventName, this.#clearOptimisticItems);
    document.addEventListener('cart:add:initiate', this.#insertOptimisticItem);
    document.addEventListener('cart:add:revert', this.#clearOptimisticItems);
    document.addEventListener('click', this.#handleTriggerClick);
    document.addEventListener('cart:update', this.#watchClosingAfterRender);
    this.#watchClosing();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(CartAddEvent.eventName, this.#clearOptimisticItems);
    document.removeEventListener('cart:add:initiate', this.#insertOptimisticItem);
    document.removeEventListener('cart:add:revert', this.#clearOptimisticItems);
    document.removeEventListener('click', this.#handleTriggerClick);
    document.removeEventListener('cart:update', this.#watchClosingAfterRender);
    this.#closeWatcher?.disconnect();
    this.#closeWatcher = null;
    window.clearTimeout(this.#closeFailsafe);
  }

  /* ════════════════ POJISTKA ZAVÍRÁNÍ (2026-09-04) ════════════════
     🔴 Nahlášeno: „odeberu z draweru poslední věc a hned kliknu mimo, aby se
     schoval — a on se otevře znova."

     Zavírání v `DialogComponent.closeDialog` (assets/dialog.js) je ŘETĚZ, který
     se dá přerušit na několika místech: přidá se třída `dialog-closing`, ČEKÁ
     se na `animationend` a teprve potom se volá `dialog.close()` a vrací se
     scroll lock z `<body>`. Když do toho okna spadne re-render košíku, může
     třída zmizet, animace se zrušit a `animationend` už nikdy nepřijde —
     dialog zůstane otevřený a navenek to vypadá, že se sám otevřel.

     ⚠️ Tady se ZÁMĚRNĚ neřeší, KDO přesně to okno rozbil (morph má vlastní
     opravu v `assets/morph.js`). Tohle je poslední instance: jakmile zavírání
     jednou začne, dialog se zavře, i kdyby animace nedoběhla. Deterministicky,
     nezávisle na příčině.

     `closeDialog` je v rodiči class field (arrow), takže ho nejde přebít a
     zavolat původní — proto se stav ČTE z DOMu přes MutationObserver místo
     override. */

  /** @type {MutationObserver | null} */
  #closeWatcher = null;

  /** @type {number} */
  #closeFailsafe = 0;

  /** Zavírání začalo (třída `dialog-closing`) a ještě neskončilo. */
  #closing = false;

  /** @type {HTMLDialogElement | null} */
  #watchedDialog = null;

  /** Po re-renderu košíku znovu navěsit hlídače (uzel mohl být vyměněn). */
  #watchClosingAfterRender = () => {
    window.setTimeout(() => this.#watchClosing(), 0);
  };

  #watchClosing() {
    const dialog = this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    // ⚠️ Re-render může `<dialog>` vyměnit za nový uzel; observer by pak visel
    // na odpojeném elementu a mlčel. Proto se hlídá identita, ne jen „už mám
    // observer".
    if (this.#closeWatcher && this.#watchedDialog === dialog) return;
    this.#closeWatcher?.disconnect();
    this.#watchedDialog = dialog;

    this.#closeWatcher = new MutationObserver(() => {
      if (dialog.classList.contains('dialog-closing')) {
        if (!this.#closing) {
          this.#closing = true;
          window.clearTimeout(this.#closeFailsafe);
          // Delší než nejdelší zavírací animace (`--animation-speed`) i s rezervou
          // na pomalý telefon; když do té doby dialog nezmizí, dořeší se tvrdě.
          this.#closeFailsafe = window.setTimeout(() => this.#forceClose(dialog), 700);
        }
        return;
      }

      if (!dialog.open) {
        // Hotovo normální cestou.
        this.#closing = false;
        window.clearTimeout(this.#closeFailsafe);
        return;
      }

      // Otevřený dialog uprostřed zavírání bez třídy = někdo ji sebral
      // (typicky re-render). Vrátit ji, ať animace i `animationend` doběhnou.
      if (this.#closing) dialog.classList.add('dialog-closing');
    });

    this.#closeWatcher.observe(dialog, { attributes: true, attributeFilter: ['open', 'class'] });
  }

  /**
   * Tvrdé dozavření včetně úklidu, který jinak dělá `closeDialog`.
   * ⚠️ Scroll lock se MUSÍ uklidit taky, jinak zůstane `<body>` fixed a stránka
   * vypadá zaseknutá. Původní pozice je uložená v `body.style.top` jako
   * `-<scrollY>px` (nastavuje ji `showDialog`), takže se dá přečíst zpátky —
   * privátní `#previousScrollY` rodiče odsud dostupné není.
   * @param {HTMLDialogElement} dialog
   */
  #forceClose(dialog) {
    this.#closing = false;
    if (!dialog.open) return;

    const top = document.body.style.top;
    const scrollY = top ? Math.abs(parseInt(top, 10)) || 0 : 0;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    if (scrollY) window.scrollTo({ top: scrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
  }

  showDialog() {
    // Otevření je vždy záměr uživatele — pojistka zavírání se ruší, ať
    // nedozavře drawer, který se právě otevřel.
    this.#closing = false;
    window.clearTimeout(this.#closeFailsafe);
    this.#watchClosing();
    super.showDialog();
  }

  /**
   * Insert an indeterminate progress bar at the top of the drawer immediately on click.
   * Removed once the real cart-items morph arrives via cart:update.
   */
  #insertOptimisticItem = () => {
    if (!this.hasAttribute('auto-open')) return;
    if (this.querySelector('.tk-cart-loading')) return;
    const inner = this.querySelector('.cart-drawer__inner');
    if (!inner) return;
    const bar = document.createElement('div');
    bar.className = 'tk-cart-loading';
    bar.setAttribute('aria-live', 'polite');
    bar.setAttribute('aria-label', 'Loading');
    inner.prepend(bar);
  };

  #clearOptimisticItems = () => {
    this.querySelectorAll('.tk-cart-loading').forEach((el) => el.remove());
  };

  /**
   * The trigger button lives in the header (snippets/cart-drawer-trigger.liquid)
   * outside this component's DOM subtree, so the declarative `on:click` binding
   * doesn't reach it. Listen at document level for clicks on the trigger.
   * @param {MouseEvent} event
   */
  #handleTriggerClick = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    if (!target?.closest) return;
    const trigger = target.closest('[data-tk-cart-trigger]');
    if (!trigger) return;
    if (trigger.getAttribute('aria-controls') !== this.id) return;
    event.preventDefault();
    this.open();
  };

  /**
   * Otevře drawer AŽ S HOTOVÝM OBSAHEM.
   *
   * ⚠️ Dřív visel `#handleCartAdd` i na `cart:add:initiate`, tedy na KLIKNUTÍ —
   * drawer vyjel okamžitě se starým obsahem košíku a přidaný produkt do něj
   * doskákal až po odpovědi serveru. Vypadalo to nedodělaně a navíc kvůli tomu
   * nebyla vidět oslava odemčení dárku (uživatel 2026-08-29).
   *
   * `CartAddEvent` nese už i vyrenderované sekce, ale morph, který je vloží do
   * DOMu, běží ve stejném kole událostí jako tenhle handler — pořadí listenerů
   * není zaručené. Proto se čeká dva snímky: než se otevře dialog, je morph
   * hotový a drawer naskočí rovnou se správným obsahem.
   *
   * Odezvu na kliknutí drží stav tlačítka „Do košíku" (`data-loading`), takže
   * odložené otevření nepůsobí jako by se nic nedělo.
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    // 🔴🔴🔴 `CartAddEvent.eventName` JE `'cart:update'` (assets/events.js:
    // `static eventName = ThemeEvents.cartUpdate`). Název třídy klame — tenhle
    // handler tedy dostane KAŽDOU změnu košíku: odebrání položky, změnu
    // množství, slevový kód, i vlastní zápisy z `tk-gift.js`. Auto-open se pak
    // pouští i tam, kde se nic nepřidalo.
    //
    // Většinou to není vidět, protože drawer už otevřený je. Projeví se to
    // přesně v jednom případě, který uživatel 2026-09-04 nahlásil třikrát:
    // odeber v draweru poslední položku a hned klikni mimo → drawer se zavře
    // správně, ale za ~300 ms dorazí odpověď serveru, tenhle handler ji vezme
    // jako „přidání" a drawer OTEVŘE ZNOVU. Reprodukováno (`open=false` ve
    // 300 ms, `open=true` + `slideInLeft` v 600 ms).
    //
    // ⚠️ ALLOW-LIST, ne deny-list: neznámý budoucí zdroj nemá otevírat drawer
    // sám od sebe. Karty a PDP pilulka drawer otevírají explicitně přes
    // `drawer.open()` (tk-product-card.js, blocks/price.liquid), takže sem
    // nepatří.
    const source = /** @type {any} */ (event)?.detail?.data?.source;
    if (source !== 'product-form-component' && source !== 'tk-fbt-add') return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.showDialog());
    });
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
